import React, { useState, useEffect, useContext } from 'react';
import axios from '../../../utils/api/axios'; // axios 인스턴스
import { useTenantList } from '../../../utils/service/tenantList';
import { useSelectedService } from '../../../utils/contextapi/ServiceProvider';
import styles from './ServiceSelector.module.css'; // ✨ CSS 모듈 생성
import { AuthContext } from '../../../utils/contextapi/AuthProvider';
import { IAM_SERVER_SERVICE } from '../../../utils/Config';
import { useLocation, useNavigate } from 'react-router-dom';
import { InnocodeChannelButton, InnocodeList, useInnocodeOverlayMenu } from '@intsoft/innocode-core';

const ServiceSelector = () => {
    const { showOverlayMenu } = useInnocodeOverlayMenu();
    const { tenantList, isLoading: isTenantListLoading } = useTenantList();
    const { selectedService, setSelectedService } = useSelectedService();
    const { role } = useContext(AuthContext);

    const [structuredMenu, setStructuredMenu] = useState([]);
    const [isLoadingServices, setIsLoadingServices] = useState(false); // 서비스 로딩 상태 추가
    const navigate = useNavigate(); // ✨ navigate 함수 가져오기

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const serviceIdFromUrl = searchParams.get('serviceId');

    // 테넌트 목록을 기반으로 서비스 목록을 가져와 메뉴 데이터 구조화
    useEffect(() => {
        const fetchAllServices = async () => {
            // 필수 정보가 없으면 실행하지 않음
            if (isTenantListLoading || !tenantList || !role) return;

            const isSysAdmin = role.includes("SYS_ADMIN");
            if (tenantList.length === 0 && !isSysAdmin) return;
            
            // 1. owner 또는 manager 권한이 있는 테넌트의 ID 목록을 추출합니다.
            const authorizedTenants = isSysAdmin
                ? tenantList
                : tenantList.filter(
                    t => t.tenantType?.toLowerCase() === 'owner' || t.tenantType?.toLowerCase() === 'manager'
                );
            const authorizedTenantIds = authorizedTenants.map(t => t.tenantId);

            // 조회할 권한이 있는 테넌트가 없으면 메뉴를 비웁니다.
            if (authorizedTenantIds.length === 0 && !isSysAdmin) {
                setStructuredMenu([]);
                return;
            }

            setIsLoadingServices(true); // 서비스 로딩 시작

            try {
                // 2. 권한에 따라 API 요청 파라미터를 구성합니다.
                let conditions = [];
                if (isSysAdmin) {
                    conditions = []; // SYS_ADMIN은 모든 서비스 조회
                } else {
                    // 일반 유저는 권한 있는 테넌트 ID 목록에 포함된 서비스만 조회
                    conditions = [{
                        field: "createdBy", // 또는 서비스와 테넌트를 연결하는 다른 필드
                        operator: "IN",
                        value: authorizedTenantIds.join(',') // 쉼표로 구분된 문자열로 전달
                    }];
                }

                const params = {
                    sortField: ["serviceName"],
                    sortOrder: ["asc"],
                    operator: "AND",
                    conditions: conditions,
                };

                // 3. 단 한 번의 API 호출로 모든 관련 서비스를 가져옵니다.
                const response = await axios.post(IAM_SERVER_SERVICE + "/all", params);
                const allServices = response.data.data.data || [];

                // 4. 받아온 전체 서비스 목록을 테넌트별로 다시 그룹화합니다.
                const servicesByTenantId = allServices.reduce((acc, service) => {
                    const tenantId = service.createdBy; // 서비스 객체에 테넌트 ID가 있다고 가정
                    if (!acc[tenantId]) {
                        acc[tenantId] = [];
                    }
                    acc[tenantId].push(service);
                    return acc;
                }, {});

                // 5. 드롭다운 메뉴에 맞는 최종 데이터 구조로 변환합니다.
                const tenantById = new Map(tenantList.map(tenant => [tenant.tenantId, tenant]));
                const menuTenantIds = isSysAdmin
                    ? [...new Set([
                        ...tenantList.map(tenant => tenant.tenantId),
                        ...allServices.map(service => service.createdBy),
                    ])]
                    : authorizedTenantIds;
                const finalMenuData = menuTenantIds
                    .filter(Boolean)
                    .map(tenantId => {
                        const tenant = tenantById.get(tenantId);
                        return {
                            tenantName: tenant?.tenantName || tenant?.createdByName || tenantId,
                            services: servicesByTenantId[tenantId] || [] // 해당 테넌트의 서비스 목록 할당
                        };
                    })
                    .filter(group => group.services.length > 0); // 서비스가 하나라도 있는 테넌트 그룹만 표시

                setStructuredMenu(finalMenuData);

                let serviceToSelect = null;

                // 2-1. URL에 serviceId가 있으면, 그 ID를 최우선으로 사용합니다.
                if (serviceIdFromUrl) {
                    // 전체 서비스 목록에서 URL의 ID와 일치하는 서비스를 찾습니다.
                    for (const group of finalMenuData) {
                        const found = group.services.find(s => s.serviceId === serviceIdFromUrl);
                        if (found) {
                            serviceToSelect = found;
                            break;
                        }
                    }
                }

                // 2-2. URL에 serviceId가 없거나, 있어도 목록에 없는 경우, localStorage 값을 확인합니다.
                if (!serviceToSelect && selectedService) {
                    // localStorage에서 복원된 selectedService가 새 목록에 존재하는지 확인
                    const currentServiceExistsInNewList = finalMenuData.some(group => 
                        group.services.some(s => s.serviceId === selectedService.serviceId)
                    );
                    if (currentServiceExistsInNewList) {
                        serviceToSelect = selectedService;
                    }
                }

                // 2-3. 위 두 경우 모두 해당하지 않으면, 목록의 첫 번째 서비스를 기본값으로 사용합니다.
                if (!serviceToSelect && finalMenuData.length > 0 && finalMenuData[0].services.length > 0) {
                    serviceToSelect = finalMenuData[0].services[0];
                }
                
                // 3. 최종적으로 결정된 서비스를 상태에 반영합니다.
                setSelectedService(serviceToSelect);

            } catch (error) {
                console.error("IN절을 사용하여 서비스 목록 조회 실패:", error);
                setStructuredMenu([]); // 실패 시 메뉴 비우기
            } finally {
                setIsLoadingServices(false); // 서비스 로딩 완료
            }
        };

        fetchAllServices();
    }, [isTenantListLoading, tenantList, role, serviceIdFromUrl]); // 의존성 배열 수정

    const handleSelectService = (service) => {
        setSelectedService(service);

        if (selectedService?.serviceId !== service.serviceId) {
            navigate(`/service/dashboard?view=service_only&serviceId=${service.serviceId}`); 
        }
    };

    const handleServiceChange = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();

        // 테넌트별로 그룹화된 메뉴 아이템 구성
        const menuItems = [];

        structuredMenu.forEach((tenantGroup, groupIndex) => {
            // 구분선 추가 (첫 번째 그룹 제외)
            if (groupIndex > 0) {
                menuItems.push({
                    label: 'divider',
                    customMenuItem: <div className={styles.divider} />,
                    disabled: true,
                    onClick: () => {} 
                });
            }

            // 테넌트 헤더 추가
            menuItems.push({
                label: tenantGroup.tenantName,
                customMenuItem: (
                    <div className={styles.tenantHeader}>
                        {tenantGroup.tenantName}
                    </div>
                ),
                disabled: true,
                onClick: () => {} 
            });

            // 해당 테넌트의 서비스 목록 추가
            tenantGroup.services.forEach(service => {
                menuItems.push({
                    label: service.serviceName,
                    customMenuItem: (
                        <InnocodeList 
                            size={"sm"}
                            borderRadius='12px'
                            className={selectedService?.serviceId === service.serviceId ? styles.selected : ''}
                        >
                            <p className={styles.serviceItemName}>{service.serviceName}</p>
                        </InnocodeList>
                    ),
                    onClick: () => {
                        handleSelectService(service);
                    }
                });
            });
        });

        showOverlayMenu({
            items: menuItems,
            position: {
                x: rect.left,
                y: rect.bottom + 10,
            },
            minWidthPx: rect.width
        });
    };

    if (isTenantListLoading || isLoadingServices) {
        return <div className={styles.selectorButton}>서비스 로딩 중...</div>;
    }

    if (!structuredMenu.length) {
        return <div className={styles.selectorButton}>선택 가능한 서비스가 없습니다.</div>;
    }

    return (
        <div className={styles.selectorWrapper}>
            <InnocodeChannelButton
                type={"person"}
                size={"sm"}
                showProfile={false}
                profile={
                    { 

                        src: selectedService?.serviceImgUrl ?? "/assets/images/service-default.png",
                        alt: selectedService?.serviceName,
                    }
                }
                title={selectedService?.serviceName}
                enableIcon={true}
                icon={
                    <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                        <path fill-rule="evenodd" d="M18.425 10.271C19.499 8.967 18.57 7 16.88 7H7.12c-1.69 0-2.618 1.967-1.544 3.271l4.881 5.927a2 2 0 0 0 3.088 0l4.88-5.927Z" clip-rule="evenodd" />
                    </svg>
                }
                onClick={handleServiceChange}
            />
        </div>
    );
};

export default ServiceSelector;
